import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core';
import {
    CustomFilterItem,
    CustomFilterType,
    PredefinedViewValue,
    CustomFilterItemValue,
} from '../../shared/components/filter/types';
import Filter, {
    ImperativeHandle,
} from '../../shared/components/filter/Filter';
import {
    getColumns,
    ColumnNamesEnum,
    getDateAddLabelValueList,
} from './Columns';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/actions';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
    filterBox: {
        padding: theme.spacing(12 / 8, 0),
    },
}));

interface Props {
    onChange: (items: PredefinedViewValue[]) => void;
    defaultCustomFilterItems?: CustomFilterItemValue[];
    persistFilter?: (
        customFilter: CustomFilterItemValue[] | null,
        predefinedViewFilter: string[] | null
    ) => void;
    currentDesignId: string;
    customerId: number | null;
    guestId: number | null;
}

const GraphicsFilter = React.forwardRef<ImperativeHandle, Props>(
    (
        {
            onChange,
            defaultCustomFilterItems,
            persistFilter,
            currentDesignId,
            customerId,
            guestId,
        },
        ref
    ) => {
        const classes = useStyles();
        const { loadTags, loadDesigns } = useTypedSelector(
            (state) => state.graphicLibrary
        );
        const dispatch = useDispatch();
        const { t } = useTranslation();

        useEffect(() => {
            if (loadTags.data === null && !loadTags.loading) {
                dispatch(actions.loadGraphicTags());
            }
            if (loadDesigns.data === null && !loadDesigns.loading) {
                dispatch(
                    actions.loadDesigns(currentDesignId, customerId, guestId)
                );
            }

            return () => {};
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        const columns = getColumns(t);

        const customFilters: CustomFilterItem[] = [
            {
                type: CustomFilterType.boolean,
                label: columns[ColumnNamesEnum.myGraphics].label,
                valueName: columns[ColumnNamesEnum.myGraphics].name,
                uniqueKey: columns[ColumnNamesEnum.myGraphics].name,
            },
            {
                type: CustomFilterType.boolean,
                label: columns[ColumnNamesEnum.accessory].label,
                valueName: columns[ColumnNamesEnum.accessory].name,
                uniqueKey: columns[ColumnNamesEnum.accessory].name,
            },
            {
                type: CustomFilterType.string,
                label: columns[ColumnNamesEnum.phrase].label,
                valueName: columns[ColumnNamesEnum.phrase].name,
                uniqueKey: columns[ColumnNamesEnum.phrase].name,
            },
            {
                type: CustomFilterType.enum,
                label: columns[ColumnNamesEnum.tags].label,
                valueName: columns[ColumnNamesEnum.tags].name,
                uniqueKey: columns[ColumnNamesEnum.tags].name,
                items:
                    loadTags.data === null
                        ? []
                        : loadTags.data.getLabelValueList(),
            },
            {
                type: CustomFilterType.boolean,
                label: columns[ColumnNamesEnum.currentDesignGraphics].label,
                valueName: columns[ColumnNamesEnum.currentDesignGraphics].name,
                uniqueKey: columns[ColumnNamesEnum.currentDesignGraphics].name,
            },
            {
                type: CustomFilterType.enum,
                label: columns[ColumnNamesEnum.design].label,
                valueName: columns[ColumnNamesEnum.design].name,
                uniqueKey: columns[ColumnNamesEnum.design].name,
                items:
                    loadDesigns.data === null
                        ? []
                        : loadDesigns.data.getLabelValueList(),
            },
            {
                type: CustomFilterType.enum,
                label: columns[ColumnNamesEnum.dateAdd].label,
                valueName: columns[ColumnNamesEnum.dateAdd].name,
                uniqueKey: columns[ColumnNamesEnum.dateAdd].name,
                items: getDateAddLabelValueList(),
            },
        ];

        return (
            <>
                <div className={classes.filterBox}>
                    <Filter
                        persistFilter={persistFilter}
                        ref={ref}
                        onChange={onChange}
                        customFilterItems={customFilters}
                        predefinedViewItems={[]}
                        name={t('Select filter')}
                        defaultCustomFilterItems={defaultCustomFilterItems}
                    />
                </div>
            </>
        );
    }
);

export default GraphicsFilter;
